import Component from '@glimmer/component';
import {
  getAllValuesForTasField,
  getCustomFieldObjectsForProvince,
  getConfigForTasField,
  getSortedOptionsByFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import type { TemplateFieldName } from '../../../utils/tuition-assistance/fields.ts';
import type TasProgramTemplate from '../../../models/tas-program-template.ts';
import TableLayout from './table-layout.gts';
import AddCustomField from './add-custom-field.gts';
import ApprovalLayoutReview from './approval-workflow/approval-layout-review.gts';
import TermsAndConditionsTable from './terms-and-conditions-table.gts';
import { t } from 'ember-intl';
import { Section } from 'tio-ui/components/layout';

interface TuitionAssistanceProgramTemplateReviewApplicationConfigReviewSignature {
  Args: {
    model: TasProgramTemplate;
  };
}

export default class TuitionAssistanceProgramTemplateReviewApplicationConfigReviewComponent extends Component<TuitionAssistanceProgramTemplateReviewApplicationConfigReviewSignature> {
  get reviewFields() {
    return this.args.model?.fields || {};
  }

  get customReviewFields(): TasProgramTemplate['customFields'] {
    return this.args.model?.customFields || {};
  }

  get employeeInfoFieldNames(): TemplateFieldName[] {
    return [
      'APPLICATION_ID',
      'NAME',
      'EMPLOYEE_ID',
      'FT_PT_OTHER',
      'EMPLOYEE_EMAIL',
      'HIRE_DATE',
      'EMPLOYEE_DOB',
      'STUDENT_ID',
    ];
  }

  get dependentInfoFieldNames(): TemplateFieldName[] {
    return ['DEPENDENT_NAME', 'DEPENDENT_DOB', 'DEPENDENT_RELATIONSHIP'];
  }

  get programInfoFieldNames(): TemplateFieldName[] {
    return [
      'SCHOOL_INSTITUTION_NAME',
      'PROGRAM_SPECIALIZATION',
      'PROGRAM_MAJOR',
      'ESTIMATED_PROGRAM_BEGIN',
      'ESTIMATED_PROGRAM_COMPLETION',
      'ESTIMATED_COST',
      'ENROLLMENT_OBJECTIVE',
    ];
  }

  get customFieldsForProgramProvince() {
    return getCustomFieldObjectsForProvince('PROGRAM', this.customReviewFields);
  }

  get customFieldsForEmployeeProvince() {
    return getCustomFieldObjectsForProvince('EMPLOYEE', this.customReviewFields);
  }

  get programAttachmentAndList() {
    const attachmentString = getConfigForTasField(
      'PROGRAM_APPROVAL_DOCUMENTS_STRING',
      this.reviewFields
    );
    const attachmentList = getConfigForTasField(
      'PROGRAM_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
      this.reviewFields
    );

    const result = {
      text: (attachmentString?.value as string) || '',
      values: attachmentList.values,
      visible: attachmentString?.visible || false,
      required: attachmentString?.required || false,
    };
    return result;
  }

  get cannedApproverResponses(): { [key: string]: TemplateFieldName } {
    return {
      response_template_for_approval: 'PROGRAM_APPROVAL_APPROVE_TEXT',
      response_template_for_rejection: 'PROGRAM_APPROVAL_REJECT_TEXT',
      response_template_for_revision: 'PROGRAM_APPROVAL_REVISE_TEXT',
    };
  }

  <template>
    <Section>
      <:header>
        {{t "common.tuition_assistance.program_template_review.program_application_setup.default"}}
      </:header>
      <:body>
        <TableLayout
          @isEditable={{false}}
          @title={{t
            "common.tuition_assistance.program_template_review.program_application_setup.employee_info"
          }}
          as |table|
        >
          {{#each this.employeeInfoFieldNames as |fieldName|}}
            <table.rowReview
              @field={{getConfigForTasField fieldName this.reviewFields}}
              @fieldOptions={{getSortedOptionsByFieldName fieldName @model.tasFieldOptions}}
            />
          {{/each}}
        </TableLayout>
        {{#if @model.isDependentProgram}}
          <TableLayout
            @isEditable={{false}}
            @title={{t "common.tuition_assistance.dependent_info"}}
            as |table|
          >
            {{#each this.dependentInfoFieldNames as |fieldName|}}
              <table.rowReview
                @field={{getConfigForTasField fieldName this.reviewFields}}
                @fieldOptions={{getSortedOptionsByFieldName fieldName @model.tasFieldOptions}}
              />
            {{/each}}
          </TableLayout>
        {{/if}}
        <AddCustomField @showCustomField={{true}} as |table|>
          {{#each this.customFieldsForEmployeeProvince as |model|}}
            <table.customRowReview @model={{model}} />
          {{/each}}
        </AddCustomField>

        <TableLayout
          @isEditable={{false}}
          @title={{t
            "common.tuition_assistance.program_template_review.program_application_setup.program_info"
          }}
          as |table|
        >
          {{#each this.programInfoFieldNames as |fieldNames|}}
            <table.rowReview
              @field={{getConfigForTasField fieldNames this.reviewFields}}
              @fieldOptions={{getSortedOptionsByFieldName fieldNames @model.tasFieldOptions}}
            />
          {{/each}}
        </TableLayout>

        <AddCustomField @showCustomField={{true}} as |table|>
          {{#each this.customFieldsForProgramProvince as |model|}}
            <table.customRowReview @model={{model}} />
          {{/each}}
        </AddCustomField>

        <ApprovalLayoutReview
          @workflowStepName={{t
            "common.tuition_assistance.program_template_review.approval_workflow.program_approval"
          }}
          @reviewFields={{this.reviewFields}}
          @workflowStepType={{t "common.tuition_assistance.program_template_review.overall"}}
          @approvalWorkflowName="PROGRAM_APPROVE_WORKFLOW"
          {{! @glint-expect-error: Type 'FieldValue[]' is not assignable to type 'TemplateApprovalWorkflowApprover[]'.}}
          @approverList={{getAllValuesForTasField "PROGRAM_APPROVE_WORKFLOW" this.reviewFields}}
          {{! @glint-expect-error: Rework this so the types are more easily parsed by ApprovalLayoutReview}}
          @attachmentData={{this.programAttachmentAndList}}
          @cannedApproverResponses={{this.cannedApproverResponses}}
          @tasFieldOptions={{@model.tasFieldOptions}}
        />

        <h2 class="font-semibold text-sm mt-8 uppercase">
          {{t "common.terms_and_conditions"}}
        </h2>
        <TermsAndConditionsTable @conditions={{@model.programApproveConditions}} />
      </:body>
    </Section>
  </template>
}
